import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Play,
  Clock,
  CheckCircle2,
  Hand,
  RefreshCw,
  AlertCircle,
  Eye,
  MessageSquare,
  FileText,
  Search,
  Upload
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useRealtimeEvent } from '@/context/NotificationContext';

const getStatusBadge = (status) => {
  switch (status) {
    case 'PENDING':
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>;
    case 'IN_PROGRESS':
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 animate-pulse">In Progress</Badge>;
    case 'WAITING':
      return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Waiting</Badge>;
    case 'COMPLETED':
      return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Completed</Badge>;
    case 'CANCELLED':
      return <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export const UserDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState({
    assigned_pending: 0,
    in_progress: 0,
    waiting: 0,
    completed_today: 0,
    total_completed: 0,
    unassigned_available: 0
  });

  const [activeWork, setActiveWork] = useState([]);
  const [unassignedList, setUnassignedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'unassigned'

  // Dialog states
  const [waitingModalOpen, setWaitingModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedRx, setSelectedRx] = useState(null);

  const [waitingReason, setWaitingReason] = useState('');
  const [quickNote, setQuickNote] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentModeError, setPaymentModeError] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, workRes, unassignedRes] = await Promise.all([
        api.get('/api/user/dashboard/stats'),
        api.get('/api/user/dashboard/my-work'),
        api.get('/api/prescriptions?unassigned=true&workflow_status=PENDING')
      ]);

      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
      if (workRes.data?.success) {
        setActiveWork(workRes.data.data || []);
      }
      setUnassignedList(Array.isArray(unassignedRes.data) ? unassignedRes.data : unassignedRes.data?.items || []);
    } catch (err) {
      console.error('Failed to load user dashboard data', err);
      toast.error('Failed to load operational workspace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Real-time automatic updates across the application (zero manual refresh needed)
  useRealtimeEvent(
    [
      'prescription.created',
      'prescription.assigned',
      'prescription.updated',
      'prescription.status_changed',
      'prescription.note_added',
    ],
    useCallback(() => {
      fetchDashboardData();
    }, [fetchDashboardData])
  );

  // Start processing
  const handleStartWork = async (rxId) => {
    try {
      const res = await api.post(`/api/prescriptions/${rxId}/status`, { status: 'IN_PROGRESS' });
      if (res.data?.success) {
        toast.success('Started processing prescription');
        fetchDashboardData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start processing');
    }
  };

  // Resume processing
  const handleResumeWork = async (rxId) => {
    try {
      const res = await api.post(`/api/prescriptions/${rxId}/status`, { status: 'IN_PROGRESS' });
      if (res.data?.success) {
        toast.success('Resumed processing prescription');
        fetchDashboardData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resume processing');
    }
  };

  // Mark as Waiting (Submit modal)
  const handleWaitingSubmit = async (e) => {
    e.preventDefault();
    if (!waitingReason.trim() || !selectedRx) {
      toast.error('Please specify a meaningful reason');
      return;
    }

    setActionSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${selectedRx.id}/status`, {
        status: 'WAITING',
        reason: waitingReason.trim()
      });
      if (res.data?.success) {
        toast.success('Prescription marked as waiting');
        setWaitingModalOpen(false);
        setWaitingReason('');
        fetchDashboardData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set waiting status');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Mark as Complete (Submit modal)
  const handleCompleteSubmit = async () => {
    if (!selectedRx) return;
    const mode = (paymentMode || '').trim().toUpperCase();
    if (!mode) {
      setPaymentModeError('Please select a payment mode before completing fulfillment.');
      return;
    }
    setPaymentModeError('');
    setActionSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${selectedRx.id}/status`, {
        status: 'COMPLETED',
        payment_mode: mode
      });
      if (res.data?.success) {
        toast.success('Prescription marked as completed! Great job.');
        setCompleteModalOpen(false);
        setPaymentMode('');
        setPaymentModeError('');
        fetchDashboardData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed to mark as completed');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Post Quick Note
  const handleQuickNoteSubmit = async (e) => {
    e.preventDefault();
    if (!quickNote.trim() || !selectedRx) return;
    setActionSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${selectedRx.id}/notes`, {
        content: quickNote.trim(),
        note_type: 'GENERAL'
      });
      if (res.data?.success) {
        toast.success('Operational note posted');
        setNoteModalOpen(false);
        setQuickNote('');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post note');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Claim unassigned prescription
  const handleClaim = async (rxId) => {
    try {
      const res = await api.post(`/api/prescriptions/${rxId}/claim`);
      if (res.data?.success) {
        toast.success('Prescription claimed into your active queue!');
        fetchDashboardData();
        setActiveTab('active');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to claim prescription');
    }
  };

  return (
    <div className="space-y-6 pb-8 md:pb-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">My Operational Queue</h1>
            <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">
              Welcome, <strong>{user?.name}</strong>. Here is your active prescription fulfillment workload for today.
            </p>
          </div>
          {/* Mobile Buttons */}
          <div className="flex items-center gap-1.5 sm:hidden flex-shrink-0">
            <Button
              variant="default"
              size="icon"
              onClick={() => navigate('/user/prescriptions/upload')}
              className="h-9 w-9"
              aria-label="Upload Prescription"
            >
              <Upload className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchDashboardData}
              disabled={loading}
              className="h-9 w-9"
              aria-label="Refresh Work"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Desktop Buttons */}
        <div className="hidden sm:flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/user/prescriptions/upload')}
            className="inline-flex items-center gap-1.5"
          >
            <Upload className="h-4 w-4" />
            Upload Prescription
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDashboardData}
            disabled={loading}
            className="inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Work
          </Button>
        </div>
      </div>

      {/* Operational KPI Metric Cards (2 columns on mobile) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-4">
        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="pb-1 pt-3 sm:pt-4 px-3 sm:px-4">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium truncate">Pending Pickup</CardDescription>
            <CardTitle className="text-xl sm:text-2xl font-bold font-serif text-amber-700">
              {stats.assigned_pending}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-2.5 sm:pb-3 text-[10px] sm:text-[11px] text-muted-foreground truncate">
            Awaiting processing
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="pb-1 pt-3 sm:pt-4 px-3 sm:px-4">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium truncate">In Progress</CardDescription>
            <CardTitle className="text-xl sm:text-2xl font-bold font-serif text-blue-700">
              {stats.in_progress}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-2.5 sm:pb-3 text-[10px] sm:text-[11px] text-muted-foreground truncate">
            Currently preparing
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="pb-1 pt-3 sm:pt-4 px-3 sm:px-4">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium truncate">Waiting</CardDescription>
            <CardTitle className="text-xl sm:text-2xl font-bold font-serif text-orange-700">
              {stats.waiting}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-2.5 sm:pb-3 text-[10px] sm:text-[11px] text-muted-foreground truncate">
            Awaiting stock / patient
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="pb-1 pt-3 sm:pt-4 px-3 sm:px-4">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium truncate">Completed Today</CardDescription>
            <CardTitle className="text-xl sm:text-2xl font-bold font-serif text-emerald-700">
              {stats.completed_today}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-2.5 sm:pb-3 text-[10px] sm:text-[11px] text-muted-foreground truncate">
            Fulfilled: {stats.total_completed}
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm min-w-0">
          <CardHeader className="pb-1 pt-3 sm:pt-4 px-3 sm:px-4">
            <CardDescription className="text-[10px] sm:text-xs uppercase font-medium truncate">Available to Claim</CardDescription>
            <div className="flex items-center justify-between gap-1">
              <CardTitle className="text-xl sm:text-2xl font-bold font-serif text-primary">
                {stats.unassigned_available}
              </CardTitle>
              {stats.unassigned_available > 0 && (
                <Badge variant="secondary" className="text-[10px] sm:text-xs font-normal bg-primary/10 text-primary px-1.5 py-0">
                  New
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-2.5 sm:pb-3 text-[10px] sm:text-[11px] text-muted-foreground truncate">
            Unassigned queue
          </CardContent>
        </Card>
      </div>

      {/* Main Work Area Tabs */}
      <Card className="border-border shadow-sm">
        <CardHeader className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('active')}
                className={`text-sm font-semibold pb-1 border-b-2 transition-colors ${
                  activeTab === 'active'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                My Active Work ({activeWork.length})
              </button>
              <span className="text-muted-foreground">•</span>
              <button
                onClick={() => setActiveTab('unassigned')}
                className={`text-sm font-semibold pb-1 border-b-2 transition-colors ${
                  activeTab === 'unassigned'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Available to Claim ({unassignedList.length})
              </button>
            </div>

            <span className="text-xs text-muted-foreground">
              {activeTab === 'active' ? 'Active prescriptions assigned to you' : 'Unassigned prescriptions awaiting assignment'}
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* ── MOBILE card list (active tab, < md) ── */}
          {activeTab === 'active' && (
            <div className="md:hidden divide-y divide-border/50">
              {loading ? (
                <div className="py-10 flex items-center justify-center text-muted-foreground">
                  <RefreshCw className="h-5 w-5 animate-spin text-primary mr-2" />
                  Loading...
                </div>
              ) : activeWork.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600/70 mx-auto mb-2" />
                  <p className="font-serif text-sm text-foreground">All caught up!</p>
                  <p className="text-xs mt-1">No active prescriptions. Check "Available to Claim".</p>
                </div>
              ) : activeWork.map((rx) => {
                const isPending = rx.workflow_status === 'PENDING';
                const isInProgress = rx.workflow_status === 'IN_PROGRESS';
                const isWaiting = rx.workflow_status === 'WAITING';
                return (
                  <div key={rx.id} className="p-4 flex flex-col gap-3">
                    {/* Row 1: Rx info + status */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{rx.patient_name}</p>
                        <p className="text-xs text-muted-foreground">Rx #{rx.id} • {rx.visit_date}</p>
                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                          {rx.medicines && rx.medicines.length > 0
                            ? rx.medicines.map(m => m.name).join(', ')
                            : rx.file_url ? 'Scanned prescription' : 'No medicines listed'}
                        </div>
                      </div>
                      {getStatusBadge(rx.workflow_status)}
                    </div>
                    {/* Row 2: Action buttons */}
                    <div className="flex gap-2 flex-wrap">
                      {isPending && (
                        <Button size="sm" className="h-10 flex-1 text-xs gap-1.5 bg-primary text-primary-foreground" onClick={() => handleStartWork(rx.id)}>
                          <Play className="h-3.5 w-3.5" /> Start Processing
                        </Button>
                      )}
                      {isWaiting && (
                        <Button size="sm" className="h-10 flex-1 text-xs gap-1.5 bg-teal-600 hover:bg-teal-700 text-white" onClick={() => handleResumeWork(rx.id)}>
                          <Play className="h-3.5 w-3.5" /> Resume
                        </Button>
                      )}
                      {isInProgress && (
                        <>
                          <Button size="sm" variant="outline" className="h-10 flex-1 text-xs gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                            onClick={() => { setSelectedRx(rx); setWaitingReason(''); setWaitingModalOpen(true); }}>
                            <Clock className="h-3.5 w-3.5" /> On Hold
                          </Button>
                          <Button size="sm" className="h-10 flex-1 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => { setSelectedRx(rx); setPaymentMode(''); setPaymentModeError(''); setCompleteModalOpen(true); }}
                            data-testid="user-open-complete-btn">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Complete
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="h-10 w-10 p-0 flex-shrink-0" title="Add Note"
                        onClick={() => { setSelectedRx(rx); setQuickNote(''); setNoteModalOpen(true); }}>
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-10 px-3 text-xs gap-1" onClick={() => navigate(`/user/prescriptions/${rx.id}`)}>
                        <Eye className="h-3.5 w-3.5" /> View
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* ── MOBILE card list (unassigned tab, < md) ── */}
          {activeTab === 'unassigned' && (
            <div className="md:hidden divide-y divide-border/50">
              {unassignedList.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground text-sm">
                  No unassigned prescriptions available to claim right now.
                </div>
              ) : unassignedList.map((rx) => (
                <div key={rx.id} className="p-4 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{rx.patient_name}</p>
                    <p className="text-xs text-muted-foreground">Rx #{rx.id} • {rx.visit_date}</p>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {rx.medicines && rx.medicines.length > 0
                        ? rx.medicines.map(m => m.name).join(', ')
                        : 'Uploaded Document'}
                    </p>
                  </div>
                  <Button size="sm" className="h-10 text-xs gap-1.5 bg-primary text-primary-foreground flex-shrink-0" onClick={() => handleClaim(rx.id)}>
                    <Hand className="h-3.5 w-3.5" /> Claim
                  </Button>
                </div>
              ))}
            </div>
          )}
          {/* ── DESKTOP: existing tables (md+) ── */}
          {activeTab === 'active' ? (
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prescription</TableHead>
                    <TableHead>Patient Details</TableHead>
                    <TableHead>Medicines</TableHead>
                    <TableHead>Current Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                        Loading your active work...
                      </TableCell>
                    </TableRow>
                  ) : activeWork.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-40 text-center text-muted-foreground">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <CheckCircle2 className="h-8 w-8 text-emerald-600/70" />
                          <span className="font-serif text-base text-foreground">You are all caught up!</span>
                          <span className="text-xs">
                            No pending or in-progress prescriptions assigned to you. Check the "Available to Claim" tab or notify the doctor.
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeWork.map((rx) => {
                      const isPending = rx.workflow_status === 'PENDING';
                      const isInProgress = rx.workflow_status === 'IN_PROGRESS';
                      const isWaiting = rx.workflow_status === 'WAITING';

                      return (
                        <TableRow key={rx.id} className="hover:bg-muted/40">
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-semibold text-foreground">Rx #{rx.id}</span>
                              <span className="text-xs text-muted-foreground">{rx.type || 'Digital'}</span>
                              <span className="text-[11px] text-muted-foreground">Visit: {rx.visit_date}</span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground">{rx.patient_name}</span>
                              <span className="text-xs text-muted-foreground">Patient #{rx.patient_id}</span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="text-xs max-w-xs truncate">
                              {rx.medicines && rx.medicines.length > 0 ? (
                                <span>{rx.medicines.map(m => m.name).join(', ')}</span>
                              ) : rx.file_url ? (
                                <span className="text-muted-foreground italic">Scanned prescription file</span>
                              ) : (
                                <span className="text-muted-foreground italic">No medicines listed</span>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            {getStatusBadge(rx.workflow_status)}
                          </TableCell>

                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5 flex-wrap">
                              {/* START BUTTON (PENDING) */}
                              {isPending && (
                                <Button
                                  size="sm"
                                  className="h-8 text-xs gap-1 bg-primary text-primary-foreground"
                                  onClick={() => handleStartWork(rx.id)}
                                >
                                  <Play className="h-3 w-3" />
                                  Start Processing
                                </Button>
                              )}

                              {/* RESUME BUTTON (WAITING) */}
                              {isWaiting && (
                                <Button
                                  size="sm"
                                  className="h-8 text-xs gap-1 bg-teal-600 hover:bg-teal-700 text-white"
                                  onClick={() => handleResumeWork(rx.id)}
                                >
                                  <Play className="h-3 w-3" />
                                  Resume
                                </Button>
                              )}

                              {/* WAITING BUTTON (IN_PROGRESS) */}
                              {isInProgress && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                                  onClick={() => {
                                    setSelectedRx(rx);
                                    setWaitingReason('');
                                    setWaitingModalOpen(true);
                                  }}
                                >
                                  <Clock className="h-3 w-3" />
                                  Waiting
                                </Button>
                              )}

                              {/* COMPLETE BUTTON (IN_PROGRESS) */}
                              {isInProgress && (
                                <Button
                                  size="sm"
                                  className="h-8 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => {
                                    setSelectedRx(rx);
                                    setPaymentMode('');
                                    setPaymentModeError('');
                                    setCompleteModalOpen(true);
                                  }}
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                  Complete
                                </Button>
                              )}

                              {/* QUICK NOTE BUTTON */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-xs"
                                title="Add Note"
                                onClick={() => {
                                  setSelectedRx(rx);
                                  setQuickNote('');
                                  setNoteModalOpen(true);
                                }}
                              >
                                <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                              </Button>

                              {/* VIEW DETAILS */}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs gap-1"
                                onClick={() => navigate(`/user/prescriptions/${rx.id}`)}
                              >
                                <Eye className="h-3 w-3" />
                                View
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            /* UNASSIGNED QUEUE TAB */
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prescription</TableHead>
                    <TableHead>Patient Details</TableHead>
                    <TableHead>Medicines / Summary</TableHead>
                    <TableHead>Visit Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unassignedList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                        No unassigned prescriptions available for claim at this moment.
                      </TableCell>
                    </TableRow>
                  ) : (
                    unassignedList.map((rx) => (
                      <TableRow key={rx.id} className="hover:bg-muted/40">
                        <TableCell>
                          <span className="font-semibold text-foreground">Rx #{rx.id}</span>
                          <div className="text-xs text-muted-foreground">{rx.type || 'Digital'}</div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-foreground">{rx.patient_name}</span>
                          <div className="text-xs text-muted-foreground">Patient #{rx.patient_id}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs max-w-xs truncate">
                            {rx.medicines && rx.medicines.length > 0 ? (
                              <span>{rx.medicines.map(m => m.name).join(', ')}</span>
                            ) : (
                              <span className="text-muted-foreground italic">Uploaded Document</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {rx.visit_date}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            className="h-8 text-xs gap-1 bg-primary text-primary-foreground"
                            onClick={() => handleClaim(rx.id)}
                          >
                            <Hand className="h-3 w-3" />
                            Claim Prescription
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MARK AS WAITING MODAL (Reason strictly required) */}
      <Dialog open={waitingModalOpen} onOpenChange={setWaitingModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              Place Prescription on Hold / Waiting
            </DialogTitle>
            <DialogDescription className="text-xs">
              A clear reason is strictly required so the doctor and clinic admin understand what is needed to resume fulfillment.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleWaitingSubmit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Hold Reason *</label>
              <Textarea
                placeholder="e.g. Ashwagandha churna currently out of stock; awaiting fresh batch from pharmacy."
                required
                className="min-h-[90px] text-xs resize-none"
                value={waitingReason}
                onChange={(e) => setWaitingReason(e.target.value)}
              />
            </div>

            <DialogFooter className="pt-2 flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" className="h-11 sm:h-10" onClick={() => setWaitingModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white h-11 sm:h-10"
                disabled={actionSubmitting || !waitingReason.trim()}
              >
                {actionSubmitting ? 'Updating...' : 'Confirm Hold'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* COMPLETE CONFIRMATION MODAL */}
      <Dialog
        open={completeModalOpen}
        onOpenChange={(open) => {
          setCompleteModalOpen(open);
          if (!open) {
            setPaymentMode('');
            setPaymentModeError('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              Complete Prescription Fulfillment
            </DialogTitle>
            <DialogDescription className="text-xs">
              Confirm that all medicines for <strong>{selectedRx?.patient_name}</strong> (Rx #{selectedRx?.id}) have been prepared, packaged, and verified.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="p-3 bg-muted/40 rounded-lg text-xs space-y-1">
              <p><strong>Patient:</strong> {selectedRx?.patient_name}</p>
              <p><strong>Medicines:</strong> {selectedRx?.medicines?.length || 0} item(s)</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dashboard-payment-mode" className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                Payment Mode <span className="text-rose-500">*</span>
              </Label>
              <Select
                value={paymentMode}
                onValueChange={(val) => {
                  setPaymentMode(val);
                  if (paymentModeError) setPaymentModeError('');
                }}
              >
                <SelectTrigger
                  id="dashboard-payment-mode"
                  data-testid="dashboard-payment-mode-select"
                  className={`h-11 sm:h-10 ${paymentModeError ? 'border-rose-500 ring-rose-500' : ''}`}
                >
                  <SelectValue placeholder="Select payment mode (Required)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="CARD">Card</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="NET_BANKING">Net Banking</SelectItem>
                </SelectContent>
              </Select>
              {paymentModeError && (
                <p className="text-xs text-rose-600 font-medium flex items-center gap-1 mt-1" role="alert" data-testid="dashboard-payment-mode-error">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {paymentModeError}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Payment mode is mandatory and will be synchronized with OneSpace ERP.
              </p>
            </div>
          </div>

          <DialogFooter className="pt-2 flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              className="h-11 sm:h-10"
              onClick={() => {
                setCompleteModalOpen(false);
                setPaymentMode('');
                setPaymentModeError('');
              }}
            >
              Back
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 sm:h-10"
              onClick={handleCompleteSubmit}
              disabled={actionSubmitting || !paymentMode}
              data-testid="confirm-complete-dashboard-btn"
            >
              {actionSubmitting ? 'Completing...' : 'Yes, Mark as Completed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QUICK NOTE MODAL */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Post Operational Note
            </DialogTitle>
            <DialogDescription className="text-xs">
              Add note to Rx #{selectedRx?.id} ({selectedRx?.patient_name}).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleQuickNoteSubmit} className="space-y-4 pt-2">
            <Textarea
              placeholder="Enter your note or remark for this prescription..."
              required
              className="min-h-[90px] text-xs resize-none"
              value={quickNote}
              onChange={(e) => setQuickNote(e.target.value)}
            />

            <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" className="h-11 sm:h-10" onClick={() => setNoteModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="h-11 sm:h-10" disabled={actionSubmitting || !quickNote.trim()}>
                {actionSubmitting ? 'Posting...' : 'Post Note'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
